import React, { useState, useEffect } from 'react';
import html2pdf from 'html2pdf.js';
import Swal from 'sweetalert2';
import axios from 'axios';

import leftLogo from '../../assets/img/logo/new-logo.png';
import rightLogo from '../../assets/img/logo/punjab-logo.png';

const upperDepartments = [
  "Cath Rotation", "ER", "Triage", "CCU-2", "CCU-1 Post cath",
  "Wing (1,2 & 7)", "Wing (5,6,8,9)"
];

const lowerDepartments = [
  "HDU-2", "OPD", "Echo / ETT", "ER rotation",
  "Daily evening ER", "Paeds Rotation", "Nuclear Radiology"
];

const translate = async (text) => {
  try {
    const res = await fetch(
      `https://translate.googleapis.com/translate_a/single?client=gtx&sl=en&tl=ur&dt=t&q=${encodeURIComponent(text)}`
    );
    const json = await res.json();
    return json?.[0]?.map((part) => part[0]).join(" ") || text;
  } catch {
    return text;
  }
};

const generatePDFBlob = async ({ language, month, departments }) => {
  const isUrdu = language === "ur";

  const allDepartments = [...upperDepartments, ...lowerDepartments];

  const translatedContent = isUrdu
    ? await Promise.all(
        allDepartments.map(async (dept) => {
          const urduDept = await translate(dept);
          const urduValue = await translate((departments[dept] || "").replace(/\n/g, '<br/>'));
          return { key: dept, dept: urduDept, value: urduValue };
        })
      )
    : allDepartments.map((dept) => ({
        key: dept,
        dept,
        value: (departments[dept] || "").replace(/\n/g, '<br/>'),
      }));

  const tableStyle = `
    @import url('https://fonts.googleapis.com/css2?family=Noto+Nastaliq+Urdu&display=swap');

    html, body {
      height: 100%;
      margin: 0;
      padding: 0;
      font-family: ${isUrdu ? "'Noto Nastaliq Urdu', serif" : "'Segoe UI', sans-serif"};
      direction: ${isUrdu ? 'rtl' : 'ltr'};
      position: relative;
      box-sizing: border-box;
    }

    .page {
      min-height: 100%;
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      padding: 40px;
    }

    table {
      width: 100%;
      border-collapse: collapse;
      font-size: 12px;
      margin-top: 16px;
      box-shadow: 0 2px 6px rgba(0,0,0,0.1);
    }

    th, td {
      border: 1px solid #ccc;
      padding: 8px;
      text-align: center;
      vertical-align: top;
    }

    th {
      background: linear-gradient(to right, #00c6ff, #0072ff);
      color: white;
      font-weight: bold;
      font-size: 14px;
    }

    tr:nth-child(even) td {
      background-color: #f0f8ff;
    }

    tr:nth-child(odd) td {
      background-color: #ffffff;
    }

    h2, h4, h5 {
      margin: 10px 0;
      text-align: center;
    }

    .watermark {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%) rotate(-30deg);
      opacity: 0.07;
      font-size: 60px;
      z-index: -1;
      white-space: nowrap;
    }

    .footer {
      margin-top: 60px;
      display: flex;
      justify-content: space-between;
      padding: 0 80px;
    }

    .signature {
      text-align: center;
    }

    .signature hr {
      width: 180px;
      margin: 0 auto 4px;
      border-top: 1.5px solid black;
    }
  `;

  const renderTable = (departmentsArray) => {
    const midpoint = Math.ceil(departmentsArray.length / 2);
    const firstRow = departmentsArray.slice(0, midpoint);
    const secondRow = departmentsArray.slice(midpoint);

    const renderRow = (row) => row.map(
      dept => `<td>${translatedContent.find(d => d.key === dept)?.value || ""}</td>`
    ).join("");

    const renderHeaderRow = (row) => row.map(
      dept => `<th>${translatedContent.find(d => d.key === dept)?.dept || dept}</th>`
    ).join("");

    return `
      <table>
        <tr>
          <th colspan="${midpoint}" style="background: linear-gradient(to right, #00c6ff, #0072ff); font-size: 15px;">
            ${isUrdu ? "پیر تا اتوار" : "Monday to Sunday"}
          </th>
        </tr>
        <tr>${renderHeaderRow(firstRow)}</tr>
        <tr>${renderRow(firstRow)}</tr>
        <tr>${renderHeaderRow(secondRow)}</tr>
        <tr>${renderRow(secondRow)}</tr>
      </table>
    `;
  };

  const content = `
  <html>
    <head>
      <style>${tableStyle}</style>
    </head>
    <body>
      <div class="page">
        <div>
          <div class="watermark">${isUrdu ? "راولپنڈی انسٹیٹیوٹ آف کارڈیالوجی" : "Rawalpindi Institute of Cardiology"}</div>

          <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 30px; padding: 0 60px;">
            <img src="${leftLogo}" style="height: 80px; margin-right: 40px;" />
            <div style="text-align: center; flex-grow: 1;">
              <h4>${isUrdu ? "راولپنڈی انسٹیٹیوٹ آف کارڈیالوجی" : "Rawalpindi Institute of Cardiology"}</h4>
              <h5>${isUrdu ? "راول روڈ، راولپنڈی" : "Rawal Road, Rawalpindi"}</h5>
              <div><strong>${month}</strong></div>
              <h3 style="margin-top: 6px;">
    ${isUrdu ? "ڈاکٹرز / پوسٹ گریجویٹ ٹرینی ڈیوٹی روسٹر / او پی ڈی" : "Medical Officers / Postgraduate Trainees Duty Roster / OPD"}
  </h3>
            </div>
            <img src="${rightLogo}" style="height: 80px; margin-left: 40px;" />
          </div>

          ${renderTable(allDepartments)}
        </div>

        <div class="footer">
          <div class="signature">
            <hr />
            <strong>Dr. Ephraim Sheraz</strong><br />
            ${isUrdu ? "اسسٹنٹ پروفیسر کارڈیالوجی" : "Assistant Professor of Cardiology"}<br />
            ${isUrdu ? "راولپنڈی انسٹیٹیوٹ آف کارڈیالوجی" : "Rawalpindi Institute of Cardiology"}<br />
            Rawalpindi
          </div>
          <div class="signature">
            <hr />
            <strong>Dr. Asim Javed</strong><br />
            ${isUrdu ? "ایسوسی ایٹ پروفیسر / سربراہ شعبہ کارڈیالوجی" : "Associate Prof. / HOD Cardiology"}<br />
            ${isUrdu ? "راولپنڈی انسٹیٹیوٹ آف کارڈیالوجی" : "Rawalpindi Institute of Cardiology"}<br />
            Rawalpindi
          </div>
        </div>
      </div>
    </body>
  </html>
  `;

  const opt = {
    margin: 0.2,
    filename: `PGT_Roster_${month}_${language.toUpperCase()}.pdf`,
    image: { type: "jpeg", quality: 0.98 },
    html2canvas: { scale: 2 },
    jsPDF: { unit: "in", format: "a4", orientation: "landscape" }
  };

  const blob = await html2pdf().from(content).set(opt).output('blob');
  return blob;
};




const RosterManager = () => {
  const [departments, setDepartments] = useState({});
  const [month, setMonth] = useState(new Date().toLocaleString("default", { month: "long", year: "numeric" }));
  const [selectedMonth, setSelectedMonth] = useState("");

  useEffect(() => {
    if (selectedMonth) fetchSavedRoster(selectedMonth);
  }, [selectedMonth]);

  const fetchSavedRoster = async (m) => {
    try {
      const res = await axios.get("https://ricgop.online/backend/pgt_roster.php", { params: { month: m } });
      if (res.data.success && res.data.departments) {
        setDepartments(res.data.departments);
        setMonth(m);
        Swal.fire("✅ Loaded", "Roster loaded for selected month.", "success");
      } else {
        Swal.fire("⚠️ No Data", `No data found for the month: ${m}`, "warning");
      }
    } catch (e) {
      Swal.fire("❌ Error", "Could not fetch roster.", "error");
    }
  };

  const handleInputChange = (dept, value) => {
    setDepartments(prev => ({ ...prev, [dept]: value }));
  };

  const downloadPDFs = async () => {
    Swal.fire({ title: 'Preparing PDFs...', text: 'Please wait...', allowOutsideClick: false, didOpen: () => Swal.showLoading() });
    try {
      const [enBlob, urBlob] = await Promise.all([
        generatePDFBlob({ language: "en", month, departments }),
        generatePDFBlob({ language: "ur", month, departments })
      ]);
      setTimeout(() => {
        const enLink = document.createElement("a");
        enLink.href = URL.createObjectURL(enBlob);
        enLink.download = `PGT_Roster_${month}_EN.pdf`;
        enLink.click();

        const urLink = document.createElement("a");
        urLink.href = URL.createObjectURL(urBlob);
        urLink.download = `PGT_Roster_${month}_UR.pdf`;
        urLink.click();

        Swal.close();
        Swal.fire("✅ Success", "PDFs downloaded.", "success");
      }, 3000);
    } catch {
      Swal.fire("❌ Error", "PDF generation failed.", "error");
    }
  };

  const emailPDFRoster = async () => {
    try {
      Swal.fire({ title: "Preparing PDFs...", html: "Please wait <b id='progress'>0</b>%", showConfirmButton: false, allowOutsideClick: false, willOpen: () => Swal.showLoading() });

      const enBlob = await generatePDFBlob({ language: "en", month, departments });
      const urBlob = await generatePDFBlob({ language: "ur", month, departments });

      const formData = new FormData();
      formData.append("englishPDF", new File([enBlob], `PGT_Roster_${month}_EN.pdf`, { type: "application/pdf" }));
      formData.append("urduPDF", new File([urBlob], `PGT_Roster_${month}_UR.pdf`, { type: "application/pdf" }));
      formData.append("month", month);

      await axios.post("https://ricgop.online/backend/email_pgt_roster.php", formData, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (e) => {
          const percent = Math.round((e.loaded * 100) / e.total);
          const el = document.getElementById("progress");
          if (el) el.textContent = percent.toString();
        },
      });

      Swal.close();
      Swal.fire("📧 Emailed", "Roster sent successfully!", "success");
    } catch {
      Swal.close();
      Swal.fire("❌ Error", "Failed to send email.", "error");
    }
  };

  const saveRoster = async () => {
    try {
      const res = await axios.post("https://ricgop.online/backend/pgt_roster.php", { month, departments });
      if (res.data.success) {
        Swal.fire("✅ Saved", "Roster saved successfully.", "success");
      } else {
        Swal.fire("❌ Error", res.data.error || "Save failed.", "error");
      }
    } catch {
      Swal.fire("❌ Error", "Save failed.", "error");
    }
  };

  return (
    <div className="bg-white p-4 p-md-5 rounded-4 shadow-lg border">
      <div className="mb-4 text-center">
        <h3 className="fw-bold text-primary mb-1">📋 Postgraduate Trainees Roster</h3>
        <p className="text-muted">Duty Station Management</p>
      </div>

      <div className="row g-4 align-items-end mb-4">
        <div className="col-md-5">
          <label className="form-label fw-semibold">📅 Select Roster Month</label>
          <select className="form-select" value={month} onChange={(e) => setMonth(e.target.value)}>
            {Array.from({ length: 24 }, (_, i) => {
              const d = new Date();
              d.setMonth(d.getMonth() + i);
              const label = d.toLocaleString("default", { month: "long", year: "numeric" });
              return <option key={i} value={label}>{label}</option>;
            })}
          </select>
        </div>

        <div className="col-md-4">
          <label className="form-label fw-semibold">🔁 Load Existing Roster</label>
          <select className="form-select" value={selectedMonth} onChange={(e) => setSelectedMonth(e.target.value)}>
            <option value="">-- Select Month --</option>
            {Array.from({ length: 36 }, (_, i) => {
              const d = new Date();
              d.setMonth(d.getMonth() - 6 + i);
              const label = d.toLocaleString("default", { month: "long", year: "numeric" });
              return <option key={i} value={label}>{label}</option>;
            })}
          </select>
        </div>

        <div className="col-md-3 d-flex flex-column gap-2">
          <button className="btn btn-success" onClick={saveRoster}>💾 Save</button>
          <button className="btn btn-outline-primary" onClick={downloadPDFs}>📥 Download</button>
          <button className="btn btn-outline-success" onClick={emailPDFRoster}>📧 Email</button>
        </div>
      </div>

      {[{ title: '🏥 Main Departments', list: upperDepartments }, { title: '🧪 Support Units', list: lowerDepartments }].map((section, sIdx) => (
        <div className="mt-4" key={sIdx}>
          <h5 className="text-secondary border-bottom pb-2 mb-3">{section.title}</h5>
          <div className="table-responsive">
            <table className="table table-hover table-bordered align-middle text-center">
              <thead className="table-light">
                <tr>{section.list.map((dept, idx) => <th key={idx}>{dept}</th>)}</tr>
              </thead>
              <tbody>
                <tr>{section.list.map((dept, idx) => (
                  <td key={idx}>
                    <textarea
                      className="form-control"
                      rows={3}
                      value={departments[dept] || ""}
                      onChange={(e) => handleInputChange(dept, e.target.value)}
                      placeholder="Write Here"
                    />
                  </td>
                ))}</tr>
              </tbody>
            </table>
          </div>
        </div>
      ))}

      {/* <div className="d-flex justify-content-between mt-5 pt-4 border-top">
        <div className="text-center">
          <hr className="mx-auto" style={{ width: '180px' }} />
          <strong className="text-muted">PGT Coordinator</strong>
        </div>
        <div className="text-center">
          <hr className="mx-auto" style={{ width: '180px' }} />
          <strong className="text-muted">Medical Director</strong>
        </div>
      </div> */}
    </div>
  );
};

export default RosterManager;
